import { useEffect, useState } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, Star, Users, TrendingUp } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function TopCreatorsSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [topCreators, setTopCreators] = useState([]);

  useEffect(() => {
    fetchTopCreators();
  }, []);

  const fetchTopCreators = async () => {
    try {
      // Get users with creator_status=true and sort by students/courses
      const response = await axios.get(`${API}/users?limit=100`);
      const creators = response.data.filter(u => u.creator_status);
      
      // Sort by reputation for now (in future: by students count)
      const sorted = creators
        .sort((a, b) => b.reputation.rating_avg - a.reputation.rating_avg)
        .slice(0, 4);
      
      setTopCreators(sorted);
    } catch (error) {
      console.error('Error fetching top creators:', error);
    }
  };

  if (topCreators.length === 0) {
    return null; // Don't show section if no creators
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 dark:from-purple-900/20 dark:via-pink-900/20 dark:to-purple-900/20 border-2 border-purple-200 dark:border-purple-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Top Creators</h3>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              Learn from the best mentors & teachers
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/courses')}
          variant="outline"
          size="sm"
          className="border-purple-500 text-purple-600 hover:bg-purple-50"
        >
          View All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {topCreators.map((creator) => (
          <div
            key={creator.id}
            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg border border-purple-100 dark:border-purple-700 hover:shadow-md transition-all cursor-pointer"
            onClick={() => navigate(`/profile/${creator.id}`)}
          >
            <Avatar className="h-14 w-14">
              <AvatarImage src={creator.picture} />
              <AvatarFallback className="bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300">
                {creator.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold text-gray-900 dark:text-white truncate">
                  {creator.name}
                </p>
                <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">
                  <Crown className="h-3 w-3" />
                </Badge>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {creator.creator_type || 'Creator'} • {creator.profile.skills_offered.slice(0, 2).join(', ')}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                  <span>{creator.reputation.rating_avg.toFixed(1)}</span>
                </div>
                <span>•</span>
                <span>{creator.reputation.total_ratings} reviews</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
